import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { withStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';

import { Field, Fields, reduxForm, SubmissionError } from 'redux-form';

import {
  DialogActions,
  DialogContent,
} from 'components/common/Dialog';
import Progress from 'components/common/Progress';
import TextField from 'components/fields/TextField';
import AutosuggestField from 'components/fields/AutosuggestField';
import FormError from 'components/common/FormError';
import Radio from '@material-ui/core/Radio';
import FormLabel from '@material-ui/core/FormLabel';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import RadioGroupField from 'components/fields/RadioGroupField';
import DatepickerField from 'components/fields/DatepickerField';
import Button from 'components/common/Button';

import {
  createInventoryStockLine,
  updateInventoryStockLine,
} from 'redux/modules/inventoryStockLine';
import {
  requestFetchFacilities,
  requestSearchFacilities,
} from 'redux/modules/facilities';
import {
  requestFetchInventoryWarehouses,
  requestSearchInventoryWarehouses,
} from 'redux/modules/inventoryWarehouses';
import { accountLoginFail } from 'redux/modules/account';

import * as validate from 'utils/validate';
import * as normalize from 'utils/normalize';

import dateFormat from 'config/dateFormat';

import InventoryStockLineCreateEditTable from './InventoryStockLineCreateEditTable';
import InventoryStockLineCreateEditTotalPriceFields from './InventoryStockLineCreateEditTotalPriceFields';
import InventoryStockLineCreateEditTotalUnitsFields from './InventoryStockLineCreateEditTotalUnitsFields';
import InventoryStockLineCreateEditUnitPriceFields from './InventoryStockLineCreateEditUnitPriceFields';
import InventoryStockLineCreateEditRetailPriceFields from './InventoryStockLineCreateEditRetailPriceFields';
import InventoryStockLineCreateEditUnitProfitFields from './InventoryStockLineCreateEditUnitProfitFields';

const styles = theme => ({
  table: {
    padding: '24px 30px',
    borderBottom: `1px solid ${theme.palette.primary.A100}`,
  },
  form: {
    padding: '8px 30px 24px',
  },
  dialogContent: {
    minHeight: 395,
  },
  grid: {
    marginTop: 10,
    '&:first-child': {
      marginTop: 0,
    },
  },
  gridActions: {
    position: 'absolute',
    left: 16,
    width: 'auto',
    marginLeft: 0,
    marginRight: 0,
  },
});

function onSubmit(values, dispatch, props) {
  const normalizeValues = (formValues) => {
    const normalizedValues = Object.assign({}, formValues, {
      ...formValues['Expiration Date'] === '' ? {
        'Expiration Date': null,
      } : {},
      ...formValues['Units in Box'] ? {
        'Quantity In Case': formValues['Units in Box'],
      } : {},
    });
    switch (formValues['Supply Type']) {
      case 'Boxes':
        if (formValues['Price per Box'] && formValues['Units in Box']) {
          normalizedValues.Price = Number(formValues['Price per Box'].toString().replace(/[$,]/g, '')) / formValues['Units in Box'];
        }
        if (formValues['Boxes Quantity'] && formValues['Units in Box']) {
          normalizedValues.Quantity = formValues['Boxes Quantity'] * formValues['Units in Box'];
        }
        break;
      case 'Kcal Units':
        if (formValues['Price per Box']
          && formValues['Units in Box']
          && formValues['Calories Per Unit']) {
          normalizedValues.Price = (Number(formValues['Price per Box'].toString().replace(/[$,]/g, ''))
            / formValues['Units in Box']
            / formValues['Calories Per Unit']) * 100;
        }
        if (formValues['Boxes Quantity']
          && formValues['Units in Box']
          && formValues['Calories Per Unit']) {
          normalizedValues.Quantity = Math.round((Number(formValues['Boxes Quantity'])
            * formValues['Units in Box']
            * formValues['Calories Per Unit']) / 100);
        }
        break;
      default:
        if (formValues['Price per Unit']) {
          normalizedValues.Price = formValues['Price per Unit'].toString().replace(/[$,]/g, '');
        }
        if (formValues['Units Quantity']) {
          normalizedValues.Quantity = formValues['Units Quantity'];
        }
    }
    if (formValues['Retail Price']) {
      normalizedValues['Retail Price'] = formValues['Retail Price'].toString().replace(/[$,]/g, '');
    } else {
      delete normalizedValues['Retail Price'];
    }
    delete normalizedValues['Supply Type'];
    delete normalizedValues['Price per Unit'];
    delete normalizedValues['Units Quantity'];
    delete normalizedValues['Boxes Quantity'];
    delete normalizedValues['Price per Box'];
    delete normalizedValues['Units in Box'];
    delete normalizedValues['Facility Name'];
    delete normalizedValues['Selected Facility Name'];

    return normalizedValues;
  };
  if (props.variant === 'edit') {
    return new Promise((resolve, reject) => {
      dispatch(updateInventoryStockLine({
        values: normalizeValues({
          ...values,
          ...values['Calories Per Unit'] !== props.initialValues['Calories Per Unit'] ? {
            'Calories Per Unit': values['Calories Per Unit'],
          } : {},
        }),
        resolve,
        reject,
      }));
    }).then(res => res,
      (err) => {
        if (err.error) {
          throw new SubmissionError({ _error: err.error });
        }
        throw new SubmissionError(err);
      });
  }
  return new Promise((resolve, reject) => {
    dispatch(createInventoryStockLine({
      values: normalizeValues({
        ...values,
        ...values['Calories Per Unit'] !== props.initialValues['Calories Per Unit'] ? {
          'Calories Per Unit': values['Calories Per Unit'],
        } : {},
      }),
      resolve,
      reject,
    }));
  }).then(res => res,
    (err) => {
      if (err.error) {
        throw new SubmissionError({ _error: err.error });
      }
      throw new SubmissionError(err);
    });
}

function onChange(values, dispatch, props) {
  if (values['Facility ID']
    && values['Selected Facility Name'] !== values['Facility Name']) {
    dispatch(props.change('Facility ID', ''));
  }
}

const InventoryStockLineCreateEditForm = ({
  handleSubmit,
  change,
  error,
  supplyType,
  actions,
  onWarehouseCreate,
  onClose,
  variant,
  submit,
  submitting,
  dirty,
  loading,
  classes,
  ...otherProps
}) => (
  <Fragment>
    <DialogContent
      className={classes.dialogContent}
      withScrollbars={loading === 0}
      disableGutters
    >
      {loading > 0 ? (
        <Progress />
      ) : (
        <Fragment>
          {otherProps['Product Name'] &&
            <InventoryStockLineCreateEditTable
              {...otherProps}
              className={classes.table}
            />
          }
          <form
            className={classes.form}
            noValidate
            autoComplete="off"
            onSubmit={handleSubmit}
          >
            {(error && typeof error === 'string') &&
              <Grid
                className={classes.grid}
                container
                spacing={24}
              >
                <Grid
                  item
                  xs={12}
                >
                  <FormError>
                    {error}
                  </FormError>
                </Grid>
              </Grid>
            }
            <Grid
              className={classes.grid}
              container
              spacing={24}
            >
              <Grid item>
                <FormControl component="fieldset">
                  <FormLabel component="legend">Supply Type</FormLabel>
                  <Field
                    name="Supply Type"
                    component={RadioGroupField}
                    direction="row"
                  >
                    <FormControlLabel
                      value="Units"
                      control={<Radio color="primary" />}
                      label="Units"
                    />
                    <FormControlLabel
                      value="Boxes"
                      control={<Radio color="primary" />}
                      label="Case (Packs)"
                    />
                    <FormControlLabel
                      value="Kcal Units"
                      control={<Radio color="primary" />}
                      label="Caloric Units"
                    />
                  </Field>
                </FormControl>
              </Grid>
            </Grid>
            {supplyType === 'Units' &&
              <Grid
                className={classes.grid}
                container
                spacing={24}
              >
                <Grid
                  item
                  xs={4}
                >
                  <Field
                    id="Units Quantity"
                    name="Units Quantity"
                    label="Units Quantity"
                    component={TextField}
                    normalize={normalize.number}
                    validate={[validate.required]}
                    errorPlacement="top"
                    fullWidth
                  />
                </Grid>
                <Grid
                  item
                  xs={4}
                >
                  <Field
                    id="Price per Unit"
                    name="Price per Unit"
                    label="Cost per Unit"
                    component={TextField}
                    normalize={normalize.price}
                    validate={[validate.required]}
                    errorPlacement="top"
                    fullWidth
                  />
                </Grid>
                <Grid
                  item
                  xs={4}
                >
                  <Fields
                    names={[
                      'Units Quantity',
                      'Price per Unit',
                    ]}
                    component={InventoryStockLineCreateEditTotalPriceFields}
                    variant="Units"
                  />
                </Grid>
              </Grid>
            }
            {supplyType === 'Boxes' &&
              <Fragment>
                <Grid
                  className={classes.grid}
                  container
                  spacing={24}
                >
                  <Grid
                    item
                    xs={4}
                  >
                    <Field
                      id="Boxes Quantity"
                      name="Boxes Quantity"
                      label="Quantity of Cases"
                      component={TextField}
                      normalize={normalize.number}
                      validate={[validate.required]}
                      errorPlacement="top"
                      fullWidth
                    />
                  </Grid>
                  <Grid
                    item
                    xs={4}
                  >
                    <Field
                      id="Price per Box"
                      name="Price per Box"
                      label="Cost per Case"
                      component={TextField}
                      normalize={normalize.price}
                      validate={[validate.required]}
                      errorPlacement="top"
                      fullWidth
                    />
                  </Grid>
                  <Grid
                    item
                    xs={4}
                  >
                    <Field
                      id="Units in Box"
                      name="Units in Box"
                      label="Units in Case"
                      component={TextField}
                      normalize={normalize.number}
                      validate={[validate.required]}
                      errorPlacement="top"
                      fullWidth
                    />
                  </Grid>
                </Grid>
              </Fragment>
            }
            {supplyType === 'Kcal Units' &&
              <Fragment>
                <Grid
                  className={classes.grid}
                  container
                  spacing={24}
                >
                  <Grid
                    item
                    xs={3}
                  >
                    <Field
                      id="Boxes Quantity"
                      name="Boxes Quantity"
                      label="Quantity of Cases"
                      component={TextField}
                      normalize={normalize.number}
                      validate={[validate.required]}
                      errorPlacement="top"
                      fullWidth
                    />
                  </Grid>
                  <Grid
                    item
                    xs={3}
                  >
                    <Field
                      id="Price per Box"
                      name="Price per Box"
                      label="Cost per Case"
                      component={TextField}
                      normalize={normalize.price}
                      validate={[validate.required]}
                      errorPlacement="top"
                      fullWidth
                    />
                  </Grid>
                  <Grid
                    item
                    xs={3}
                  >
                    <Field
                      id="Units in Box"
                      name="Units in Box"
                      label="Units in Case"
                      component={TextField}
                      normalize={normalize.number}
                      validate={[validate.required]}
                      errorPlacement="top"
                      fullWidth
                    />
                  </Grid>
                  <Grid
                    item
                    xs={3}
                  >
                    <Field
                      id="Calories Per Unit"
                      name="Calories Per Unit"
                      label="Calories per Unit"
                      component={TextField}
                      normalize={normalize.number}
                      validate={[validate.required]}
                      errorPlacement="top"
                      fullWidth
                    />
                  </Grid>
                </Grid>
              </Fragment>
            }
            <Fields
              names={[
                'Retail Price',
                'Is Retail Price For Claims',
                'Is Retail Price For Self Pay',
              ]}
              component={InventoryStockLineCreateEditRetailPriceFields}
            />
            <Grid
              className={classes.grid}
              container
              spacing={24}
            >
              <Grid
                item
                xs={4}
              >
                <Field
                  id="Facility Name"
                  name="Facility Name"
                  component={AutosuggestField}
                  label="Facility"
                  placeholder="-Select Facility-"
                  fullWidth
                  renderSuggestionContent={suggestion => suggestion['Company Info']['Company Name']}
                  onSuggestionSelected={(suggestion) => {
                    change('Facility ID', suggestion.ID);
                    change('Selected Facility Name', suggestion['Company Info']['Company Name']);
                    return suggestion['Company Info']['Company Name'];
                  }}
                  fetchSuggestions={({ value, limit, offset }) => (
                    value.length > 0
                      ? requestSearchFacilities({
                        filters: [
                          {
                            name: 'Facility Name',
                            operation: 'startswith',
                            val: value,
                            case_sensitivity: false,
                          },
                        ],
                        limit,
                        offset,
                      }).then(res => res.data,
                        (err) => {
                          if (err.data.error === 'Need authorisation') {
                            actions.accountLoginFail();
                          }
                          return err;
                        })
                      : requestFetchFacilities({
                        limit,
                        offset,
                      }).then(res => res.data,
                        (err) => {
                          if (err.data.error === 'Need authorisation') {
                            actions.accountLoginFail();
                          }
                          return error;
                        })
                  )}
                />
              </Grid>
              <Grid
                item
                xs={4}
              >
                <Field
                  id="Warehouse Location"
                  name="Warehouse Location"
                  label="Warehouse Location"
                  component={AutosuggestField}
                  fullWidth
                  errorPlacement="top"
                  listWidth="auto"
                  renderSuggestionContent={suggestion => suggestion.Location}
                  onSuggestionSelected={(suggestion) => {
                    change('Warehouse ID', suggestion.ID);
                    change('Selected Warehouse Location', suggestion.Location);
                    return suggestion.Location;
                  }}
                  fetchSuggestions={({ value, limit, offset }) => (
                    value.length > 0
                      ? requestSearchInventoryWarehouses({
                        filters: [
                          {
                            name: 'Location',
                            operation: 'startswith',
                            val: value,
                            case_sensitivity: false,
                          },
                        ],
                        limit,
                        offset,
                      }).then(res => res.data,
                        (err) => {
                          if (err.data.error === 'Need authorisation') {
                            actions.accountLoginFail();
                          }
                          return err;
                        })
                      : requestFetchInventoryWarehouses({
                        limit,
                        offset,
                      }).then(res => res.data,
                        (err) => {
                          if (err.data.error === 'Need authorisation') {
                            actions.accountLoginFail();
                          }
                          return err;
                        })
                  )}
                  onListAdornmentClick={onWarehouseCreate}
                  listAdornmentText="Add New"
                />
              </Grid>
              <Grid
                item
                xs={4}
              >
                <Field
                  id="Expiration Date"
                  name="Expiration Date"
                  label="Expiration Date"
                  component={DatepickerField}
                  validate={[validate.date]}
                  normalize={normalize.date}
                  fullWidth
                  minDate="01/01/1900"
                  maxDate="01/01/2100"
                  dateFormat={dateFormat}
                  placeholder="mm/dd/yyyy"
                  endIcon="end"
                  errorPlacement="bottom"
                  datepickerPlacement="top-left"
                />
              </Grid>
            </Grid>
          </form>
        </Fragment>
      )}
      </DialogContent>
      <DialogActions>
        {supplyType === 'Units' &&
          <Grid
            className={classes.gridActions}
            container
            spacing={24}
          >
            <Fields
              names={[
                'Units Quantity',
                'Price per Unit',
              ]}
              component={InventoryStockLineCreateEditTotalPriceFields}
              variant="Units"
            />
            <Fields
              names={[
                'Price per Unit',
                'Retail Price',
              ]}
              component={InventoryStockLineCreateEditUnitProfitFields}
              variant="Units"
            />
          </Grid>
        }
        {supplyType === 'Boxes' &&
          <Grid
            className={classes.gridActions}
            container
            spacing={24}
          >
            <Fields
              names={[
                'Boxes Quantity',
                'Units in Box',
              ]}
              component={InventoryStockLineCreateEditTotalUnitsFields}
              variant="Boxes"
            />
            <Fields
              names={[
                'Price per Box',
                'Units in Box',
              ]}
              component={InventoryStockLineCreateEditUnitPriceFields}
              variant="Boxes"
            />
            <Fields
              names={[
                'Boxes Quantity',
                'Price per Box',
              ]}
              component={InventoryStockLineCreateEditTotalPriceFields}
              variant="Boxes"
            />
            <Fields
              names={[
                'Price per Box',
                'Units in Box',
                'Retail Price',
              ]}
              component={InventoryStockLineCreateEditUnitProfitFields}
              variant="Boxes"
            />
          </Grid>
        }
        {supplyType === 'Kcal Units' &&
          <Grid
            className={classes.gridActions}
            container
            spacing={24}
          >
            <Fields
              names={[
                'Boxes Quantity',
                'Units in Box',
                'Calories Per Unit',
              ]}
              component={InventoryStockLineCreateEditTotalUnitsFields}
              variant="Kcal Units"
            />
            <Fields
              names={[
                'Price per Box',
                'Units in Box',
                'Calories Per Unit',
              ]}
              component={InventoryStockLineCreateEditUnitPriceFields}
              variant="Kcal Units"
            />
            <Fields
              names={[
                'Boxes Quantity',
                'Price per Box',
              ]}
              component={InventoryStockLineCreateEditTotalPriceFields}
              variant="Boxes"
            />
            <Fields
              names={[
                'Price per Box',
                'Units in Box',
                'Calories Per Unit',
                'Retail Price',
              ]}
              component={InventoryStockLineCreateEditUnitProfitFields}
              variant="Kcal Units"
            />
          </Grid>
        }
      <Button
        onClick={onClose}
        variant="raised"
      >
        Cancel
      </Button>
      <Button
        onClick={() => {
          if (variant === 'edit' && !dirty) {
            onClose();
          } else {
            submit();
          }
        }}
        variant="raised"
        color="primary"
        disabled={submitting}
      >
        {variant === 'create' ? 'Add' : 'Save'}
      </Button>
    </DialogActions>
  </Fragment>
);

InventoryStockLineCreateEditForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  change: PropTypes.func.isRequired,
  error: PropTypes.string,
  className: PropTypes.string,
  supplyType: PropTypes.oneOf(['Units', 'Boxes', 'Kcal Units']),
  onWarehouseCreate: PropTypes.func,
  actions: PropTypes.shape({
    accountLoginFail: PropTypes.func.isRequired,
  }).isRequired,
  classes: PropTypes.shape({
    grid: PropTypes.string.isRequired,
  }).isRequired,
};

InventoryStockLineCreateEditForm.defaultProps = {
  className: '',
  error: null,
  supplyType: 'Units',
  onWarehouseCreate: () => {},
};

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({ accountLoginFail }, dispatch),
});

export default connect(null, mapDispatchToProps)(reduxForm({
  form: 'inventoryStockLineCreate',
  onSubmit,
  onChange,
})(withStyles(styles)(InventoryStockLineCreateEditForm)));



// WEBPACK FOOTER //
// ./src/components/inventory/InventoryStockLineCreateEditDialog/InventoryStockLineCreateEditForm.js